<template>
  <CatalogoPagina
    titulo="Inventario" prefijo="inventario" :resumen="resumen" :columnas="columnas"
    :total="total" :pagina="paginaActual" :total-paginas="totalPaginas" :limite="limite"
    :cargando="cargando" :ya-cargo="yaCargo" :error="errorCarga"
    mensaje-error="No pudimos cargar el inventario." :aviso="aviso"
    @limite="alCambiarLimite" @pagina="cambiarPagina" @reintentar="initialize" @cerrar-aviso="aviso = ''"
  >
    <template #filtros>
      <AppBusqueda v-model="busqueda" placeholder="Buscar producto del inventario" testid="inventario-buscar" @buscar="aplicarFiltros" />
      <button v-if="busqueda" type="button" class="cat-limpiar" @click="limpiarFiltros">Limpiar filtros</button>
    </template>
    <template #filas="{ columnaVisible }">
      <tr v-for="item in inventario" :key="item._id" data-testid="inventario-fila">
        <td>
          <div class="cat-pila">
            <span class="cat-nombre-texto iv-nombre" :title="item.product && item.product.name" data-testid="inventario-nombre">{{ item.product && item.product.name || 'Producto no disponible' }}</span>
            <span class="cat-meta">{{ marcaDe(item) }}<template v-if="item.product && item.product.description"> · {{ item.product.description }}</template></span>
          </div>
        </td>
        <td><span class="iv-etiqueta">Existencia: </span>{{ numero(item.stock) }}</td>
        <td><span class="iv-etiqueta">Límites: </span>{{ numero(item.stock_min) }}–{{ numero(item.stock_max) }}</td>
        <td v-if="columnaVisible('precios')"><span class="iv-etiqueta">Compra / venta: </span>{{ precio(item.product && item.product.purchase_price) }} / {{ precio(item.product && item.product.sale_price) }}</td>
        <td>{{ item.warehouse && item.warehouse.description || 'Sin almacén' }}</td>
        <td v-if="columnaVisible('updatedAt')">{{ fecha(item.updatedAt) }}</td>
        <td class="cat-fila-acciones">
          <button type="button" class="cat-icono-btn" :aria-label="'Ver detalle de ' + (item.product && item.product.name || 'producto')" data-testid="inventario-detalle" @click="detalle = item"><AppIcon name="eye" size="h-4 w-4" /></button>
          <button type="button" class="cat-icono-btn" :aria-label="'Editar límites de ' + (item.product && item.product.name || 'producto')" data-testid="inventario-editar" @click="editar(item, $event)">
            <AppIcon name="pencil" size="h-4 w-4" />
          </button>
        </td>
      </tr>
    </template>
    <template #vacio><p>{{ busqueda ? 'No hay productos que coincidan con la búsqueda.' : 'No hay productos en el inventario.' }}</p></template>
    <BaseModal :visible="dialog" title="Editar límites del inventario" max-width="max-w-lg" persistent @close="cerrar">
      <form id="inventario-form" novalidate @submit.prevent="guardar">
        <p class="iv-producto">{{ seleccionado && seleccionado.product && seleccionado.product.name }}</p>
        <p class="iv-ayuda">La existencia se actualiza con compras y ventas. Aquí puedes modificar sus límites.</p>
        <div class="iv-form">
          <AppField v-model="form.stock_min" label="Cantidad mínima" type="number" testid="inventario-minimo" :error="intentado ? errores.stock_min : ''" />
          <AppField v-model="form.stock_max" label="Cantidad máxima" type="number" testid="inventario-maximo" :error="intentado ? errores.stock_max : ''" />
        </div>
        <p v-if="errorGuardar" class="iv-error" role="alert" data-testid="inventario-error-guardar">{{ errorGuardar }}</p>
      </form>
      <template #footer>
        <button type="button" class="rm-btn" :disabled="guardando" @click="cerrar">Cancelar</button>
        <button type="submit" form="inventario-form" class="rm-btn rm-btn-primario" :disabled="guardando" data-testid="inventario-guardar">{{ guardando ? 'Guardando…' : 'Guardar límites' }}</button>
      </template>
    </BaseModal>
    <BaseModal :visible="Boolean(detalle)" title="Detalle del inventario" max-width="max-w-lg" @close="detalle = null">
      <dl v-if="detalle" class="iv-detalle">
        <dt>Producto</dt><dd>{{ detalle.product && detalle.product.name || 'Producto no disponible' }}</dd>
        <dt>Descripción</dt><dd>{{ detalle.product && detalle.product.description || 'Sin descripción' }}</dd>
        <dt>Marca</dt><dd>{{ marcaDe(detalle) }}</dd>
        <dt>Existencia</dt><dd>{{ numero(detalle.stock) }}</dd>
        <dt>Mínima / máxima</dt><dd>{{ numero(detalle.stock_min) }} / {{ numero(detalle.stock_max) }}</dd>
        <dt>Compra</dt><dd>{{ precio(detalle.product && detalle.product.purchase_price) }}</dd>
        <dt>Venta</dt><dd>{{ precio(detalle.product && detalle.product.sale_price) }}</dd>
        <dt>Almacén</dt><dd>{{ detalle.warehouse && detalle.warehouse.description || 'Sin almacén' }}</dd>
        <dt>Actualización</dt><dd>{{ fecha(detalle.updatedAt) }}</dd>
      </dl>
    </BaseModal>
  </CatalogoPagina>
</template>

<script>
import CatalogoPagina from '@/components/catalogo/CatalogoPagina.vue'
import AppBusqueda from '@/components/ui/AppBusqueda.vue'
import AppIcon from '@/components/ui/AppIcon.vue'
import AppField from '@/components/ui/AppField.vue'
import BaseModal from '@/components/ui/BaseModal.vue'
import Catalogos from '@/services/catalogos'
import { paginaEquivalente } from '@/utils/paginacion'

export default {
  name: 'Inventario',
  components: { CatalogoPagina, AppBusqueda, AppIcon, AppField, BaseModal },
  data: () => ({
    inventario: [], total: 0, paginaActual: 1, totalPaginas: 1, limite: 8,
    busqueda: '', cargando: false, yaCargo: false, errorCarga: false, aviso: '',
    dialog: false, detalle: null, seleccionado: null, form: { stock_min: '', stock_max: '' },
    intentado: false, guardando: false, errorGuardar: '',
    // Ancho fijo en las columnas cortas: el resto queda para el nombre del producto
    columnas: [
      { value: 'producto', label: 'Producto', ordenable: false },
      { value: 'stock', label: 'Existencia', ordenable: false, ancho: '112px' },
      { value: 'limites', label: 'Mínima / máxima', ordenable: false, ancho: '144px' },
      { value: 'precios', label: 'Compra / venta', ordenable: false, ocultableAngosto: true, ancho: '184px' },
      { value: 'almacen', label: 'Almacén', ordenable: false, ancho: '128px' },
      { value: 'updatedAt', label: 'Actualización', ordenable: false, ocultableAngosto: true, ancho: '144px' },
    ],
  }),
  computed: {
    resumen() { return `${this.total} ${this.busqueda.trim() ? 'coincidencias' : 'productos en inventario'}` },
    errores() {
      const errors = {}
      for (const key of ['stock_min', 'stock_max']) {
        const value = this.form[key]
        if (value !== '' && value != null && !Number.isFinite(Number(value))) errors[key] = 'Ingresa una cantidad numérica válida.'
      }
      return errors
    },
  },
  created() { this.ultimaConsulta = 0; this.limiteListo = false },
  beforeDestroy() { this.ultimaConsulta++; clearTimeout(this.avisoTimer) },
  methods: {
    numero(value) { return value == null || value === '' || !Number.isFinite(Number(value)) ? '—' : Number(value).toLocaleString('es-MX') },
    precio(value) { return value == null || value === '' || !Number.isFinite(Number(value)) ? '—' : Number(value).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' }) },
    marcaDe(item) { return item.product?.brand?.name || item.brand?.name || 'Sin marca' },
    fecha(value) { const date = new Date(value); return !value || Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('es-MX') },
    async initialize() {
      const consulta = ++this.ultimaConsulta
      this.cargando = true; this.errorCarga = false
      try {
        const res = await Catalogos.inventarioPaginado({ pagina: this.paginaActual, limite: this.limite, busqueda: this.busqueda.trim() })
        if (consulta !== this.ultimaConsulta) return
        if (!Array.isArray(res?.items)) throw new Error('Respuesta inválida')
        this.inventario = res.items; this.total = res.total; this.totalPaginas = res.totalPages; this.yaCargo = true
        if (this.paginaActual > this.totalPaginas) { this.paginaActual = this.totalPaginas; return this.initialize() }
      } catch (error) {
        if (consulta !== this.ultimaConsulta) return
        this.errorCarga = true; this.yaCargo = false; this.inventario = []; this.total = 0
      } finally { if (consulta === this.ultimaConsulta) this.cargando = false }
    },
    alCambiarLimite(value) {
      if (this.limiteListo && value === this.limite) return
      this.limiteListo = true
      this.paginaActual = paginaEquivalente(this.paginaActual, this.limite, value)
      this.limite = value; this.initialize()
    },
    cambiarPagina(value) { this.paginaActual = value; this.initialize() },
    aplicarFiltros() { this.paginaActual = 1; this.initialize() },
    limpiarFiltros() { this.busqueda = ''; this.aplicarFiltros() },
    editar(item, event) {
      this.focoAnterior = event?.currentTarget
      this.seleccionado = item; this.form = { stock_min: item.stock_min ?? '', stock_max: item.stock_max ?? '' }
      this.intentado = false; this.errorGuardar = ''; this.dialog = true
      this.$nextTick(() => this.$el.querySelector('[data-testid="inventario-minimo"]')?.focus())
    },
    cerrar() {
      if (this.guardando) return
      this.dialog = false
      this.$nextTick(() => this.focoAnterior?.focus())
    },
    async guardar() {
      this.intentado = true
      if (this.guardando || !this.seleccionado || Object.keys(this.errores).length) return
      this.guardando = true; this.errorGuardar = ''
      try {
        await Catalogos.inventarioEditar(this.seleccionado._id, { stock_min: this.form.stock_min === '' || this.form.stock_min == null ? null : Number(this.form.stock_min), stock_max: this.form.stock_max === '' || this.form.stock_max == null ? null : Number(this.form.stock_max) })
        this.guardando = false; this.cerrar(); this.aviso = 'Límites del inventario actualizados.'
        clearTimeout(this.avisoTimer); this.avisoTimer = setTimeout(() => { this.aviso = '' }, 5000)
        await this.initialize()
      } catch (error) { this.errorGuardar = error.response?.data?.message || error.data?.message || (typeof error.data === 'string' ? error.data : '') || 'No pudimos guardar los límites. Intenta de nuevo.' }
      finally { this.guardando = false }
    },
  },
}
</script>

<style scoped>
.cat-pagina ::v-deep .cat-tarjeta { --cat-alto-fila: 76px; }
/* El nombre llega a dos líneas y la fila mide igual con una o dos (76px) */
.cat-pagina .cat-nombre-texto.iv-nombre { display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; line-height: 1.3; white-space: normal; overflow-wrap: anywhere; }
.iv-form { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.iv-producto { font-weight: 700; color: #0b1b2e; }
.iv-ayuda { color: #607187; font-size: 14px; margin-bottom: 18px; }
.iv-error { margin-top: 16px; color: #b91c1c; }
.iv-etiqueta { display: none; }
.iv-detalle { display: grid; grid-template-columns: 1fr 2fr; gap: 12px; }
.iv-detalle dt { font-weight: 600; }
.iv-detalle dd { margin: 0; overflow-wrap: anywhere; }
@media (max-width: 700px) { .cat-pagina .cat-fila-acciones { flex-wrap: wrap; } }
@media (max-width: 700px) { .iv-etiqueta { display: inline; color: #607187; } }
@media (max-width: 480px) { .iv-form { grid-template-columns: 1fr; } }
</style>
